import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { uploadFile } from "@redotech/redo-api-client/conversations";
import { useTranslation } from "@redotech/redo-i18n";
import FaceSmile from "@redotech/redo-icons/face-smile.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { Attachment } from "@redotech/redo-support-common/create-conversation-body";
import { conversationFileUploadErrorMessages } from "@redotech/redo-support-common/support/conversations/conversation-file-upload-error";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { AttachmentThumbnail } from "@redotech/redo-web/attachment-thumbnail";
import { RedoClientContext } from "@redotech/redo-web/client";
import { Flex } from "@redotech/redo-web/flex";
import PaperclipIcon from "@redotech/redo-web/icon-old/paperclip.svg";
import { QuillEditor } from "@redotech/redo-web/quill/quill-editor";
import * as quillEditorCss from "@redotech/redo-web/quill/quill-editor.module.css";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { nativeRandom, randomHexString } from "@redotech/util/random";
import { browserIsMac } from "@redotech/web-util/browser";
import Quill from "quill";
import { memo, useMemo, useRef, useState } from "react";
import * as stepRichTextCss from "./step-rich-text.module.css";

export const StepRichTextEditor = memo(function StepRichTextEditor({
  quillRef,
  htmlValue,
  textValue,
  onTextChange,
  readOnly,
  enableAttachments,
  attachments,
  onAttachmentUpload,
  onAttachmentRemoval,
}: {
  quillRef: React.MutableRefObject<Quill | undefined>;
  htmlValue?: string;
  textValue: string;
  onTextChange: () => void;
  readOnly?: boolean;
  enableAttachments?: boolean;
  attachments?: Attachment[];
  onAttachmentUpload?: (attachment: Attachment) => void;
  onAttachmentRemoval?: (url: string) => void;
}) {
  const { t } = useTranslation();
  const apiClient = useRequiredContext(RedoClientContext);
  const [localAttachments, setLocalAttachments] = useState<Attachment[]>(
    attachments || [],
  );

  const toolbarId = useMemo<string>(
    () => `toolbar-${randomHexString(nativeRandom, 8)}`,
    [],
  );

  const emojiPickerAnchorRef = useRef<HTMLDivElement | null>(null);

  const setQuillRef = useHandler((ref: Quill | null) => {
    quillRef.current = ref || undefined;
  });

  async function handleUpload({ event, file }: { event?: any; file?: File }) {
    const fileToUpload = file || event.target?.files?.[0];
    if (!fileToUpload) {
      return;
    }

    const form = new FormData();
    form.append("file", fileToUpload);
    form.append("fileName", fileToUpload.name);
    const response = await uploadFile(apiClient, form);
    if (response.success) {
      const body = response.body;
      const newAttachment = {
        url: body.url,
        description: body.description,
        mimeType: body.mimeType,
      };
      if (onAttachmentUpload) {
        onAttachmentUpload(newAttachment);
      }
      setLocalAttachments((oldAttachments) => {
        return [newAttachment, ...oldAttachments];
      });
    } else {
      toast(
        conversationFileUploadErrorMessages?.[response.error] ??
          t("Unknown error uploading file."),
        { variant: "error" },
      );
    }
  }

  function clearInput(e: any) {
    // This makes the onChange always get triggered.
    e.target.value = "";
  }

  function removeFileFromAttachments(url: string) {
    if (onAttachmentRemoval) {
      onAttachmentRemoval(url);
    }
    setLocalAttachments((oldAttachments) => {
      return oldAttachments.filter((attachment) => attachment.url !== url);
    });
  }

  const isMac = browserIsMac();

  return (
    <Flex
      borderColor="secondary"
      borderRadius="lg"
      borderWidth="1px"
      dir="column"
      ref={emojiPickerAnchorRef}
    >
      <Flex className={quillEditorCss.quillToolbar} id={toolbarId}>
        <Flex align="center" wrap="wrap">
          <button className="ql-bold" />
          <button className="ql-italic" />
          <button className="ql-underline" />
          <button className="ql-link" />
          <button className="ql-strike" />
          <button className="ql-list" value="ordered" />
          <button className="ql-list" value="bullet" />
          <select className="ql-size" defaultValue="16px">
            <option value="12px">{t("Small", { context: "font size" })}</option>
            <option value="16px">
              {t("Normal", { context: "font size" })}
            </option>
            <option value="24px">{t("Large", { context: "font size" })}</option>
            <option value="36px">{t("Huge", { context: "font size" })}</option>
          </select>
          <select className="ql-color" />
          {enableAttachments && (
            <>
              {/* TODO: re-enable when we can figure out how to get the file picker to show */}
              {/* <button className="ql-image" /> */}
              <Flex
                as="label"
                id="file-upload-label"
                labelProps={{ htmlFor: "file-upload" }}
              >
                <RedoButton
                  aria-label={t("Attach file")}
                  hierarchy="tertiary"
                  IconLeading={PaperclipIcon}
                  onClick={() => {
                    (
                      document.querySelector(
                        `#${toolbarId} #file-upload`,
                      ) as HTMLElement | null
                    )?.click();
                  }}
                />
              </Flex>
              <input
                id="file-upload"
                onChange={(e) => handleUpload({ event: e })}
                onClick={(e) => clearInput(e)}
                style={{ display: "none" }}
                type="file"
              />
            </>
          )}
          <Tooltip
            title={t("Insert emoji ({{shortcut}}+3)", {
              context: "tooltip; plus joins the modifier key and the 3 key",
              shortcut: isMac ? "⌘" : "Ctrl",
            })}
          >
            <RedoButton
              aria-label={t("Insert emoji")}
              className="rql-emoji"
              hierarchy="tertiary"
              IconLeading={FaceSmile}
            />
          </Tooltip>
        </Flex>
      </Flex>
      <QuillEditor
        defaultHtmlContents={htmlValue || textValue}
        editorClassName={quillEditorCss.quillEditorSmall}
        emojiPickerAnchorOverride={emojiPickerAnchorRef.current}
        onTextChange={onTextChange}
        readOnly={readOnly}
        ref={setQuillRef}
        toolbarId={toolbarId}
      />
      {!!localAttachments.length && (
        <Flex
          className={stepRichTextCss.fileList}
          dir="column"
          gap="sm"
          overflowX="auto"
        >
          {localAttachments.map((attachment) => (
            <Flex
              className={stepRichTextCss.attachmentContainer}
              key={attachment.url}
              position="relative"
            >
              <AttachmentThumbnail
                description={attachment.description}
                mimeType={attachment.mimeType}
                restrictWidth
                url={attachment.url}
              />
              <Flex
                align="center"
                bgColor="error-primary"
                borderRadius="full"
                className={stepRichTextCss.removeFileButton}
                cursor="pointer"
                justify="center"
                position="absolute"
              >
                <RedoButton
                  aria-label={t("Remove file")}
                  hierarchy="tertiary"
                  IconLeading={XIcon}
                  onClick={() => removeFileFromAttachments(attachment.url)}
                  size="xs"
                  theme="destructive"
                />
              </Flex>
            </Flex>
          ))}
        </Flex>
      )}
    </Flex>
  );
});
